import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../Assets/assetsShared";
import "./EmployeeLog.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const VIEWS = [
  { key: "year", label: "Year wise" },
  { key: "month", label: "Month wise" },
  { key: "category", label: "Category wise" },
  { key: "entries", label: "All entries" },
];

const thisYear = () => String(new Date().getFullYear());
const fmt = (n) => Number(Number(n || 0).toFixed(2));
const monthOf = (e) => String(e.date || "").slice(5, 7);
const sumHours = (list) => list.reduce((s, e) => s + Number(e.hours || 0), 0);

// "13:05" -> "01:05 PM"
const showTime = (value) => {
  if (!value) return "";
  const [h, m] = String(value).split(":").map(Number);
  const hour = h % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${
    h >= 12 ? "PM" : "AM"
  }`;
};

export default function EmployeeLog() {
  const [view, setView] = useState("month");
  const [year, setYear] = useState(thisYear());
  const [month, setMonth] = useState("all"); // "01".."12" or "all"
  const [employee, setEmployee] = useState("all");

  const [years, setYears] = useState([]); // [{ year, entries, hours }]
  const [isAdmin, setIsAdmin] = useState(false); // true when the user can see all employees
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const opened = useRef(false);

  // the employee wise tab always loads everyone
  const userParam =
    view === "employee" || employee === "all" ? undefined : employee;

  /* ---------- employees list (only for users who can see all) ---------- */
  useEffect(() => {
    api
      .get("/timesheet/log/employees")
      .then((res) => setEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEmployees([]));
  }, []);

  /* ---------- year wise totals ---------- */
  useEffect(() => {
    api
      .get("/timesheet/log/summary", { params: { user: userParam } })
      .then((res) => {
        const list = res.data?.years || [];
        const canSeeAll = Boolean(res.data?.isAdmin);

        setYears(list);
        setIsAdmin(canSeeAll);

        // open the Employee wise tab the first time for users who can see all
        if (canSeeAll && !opened.current) {
          opened.current = true;
          setView("employee");
        }

        // no entries in the selected year: jump to the latest year that has data
        setYear((prev) =>
          !list.length || list.some((y) => y.year === prev)
            ? prev
            : list[0].year,
        );
      })
      .catch(() => setMessage("Could not load the year wise totals."));
  }, [employee, view]); // eslint-disable-line

  /* ---------- entries of the selected year ---------- */
  useEffect(() => {
    setLoading(true);
    setMessage("");

    api
      .get("/timesheet/log", { params: { year, user: userParam } })
      .then((res) => setEntries(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        setEntries([]);
        setMessage("Could not load the log.");
      })
      .finally(() => setLoading(false));
  }, [year, employee, view]); // eslint-disable-line

  const yearOptions = useMemo(
    () =>
      Array.from(new Set([...years.map((y) => y.year), thisYear(), year])).sort(
        (a, b) => b.localeCompare(a),
      ),
    [years, year],
  );

  /* ---------- month filter applies to employee wise, category wise and all entries ---------- */
  const showMonthFilter =
    view === "employee" || view === "category" || view === "entries";

  const filtered =
    showMonthFilter && month !== "all"
      ? entries.filter((e) => monthOf(e) === month)
      : entries;

  const totalHours = sumHours(filtered);

  /* ---------- employee wise rows (everyone, also those without entries) ---------- */
  const employeeRows = useMemo(() => {
    const byUser = new Map();

    filtered.forEach((e) => {
      const id = String(e.user);
      const g = byUser.get(id) || {
        name: e.employee,
        entries: 0,
        hours: 0,
        days: new Set(),
      };
      g.entries += 1;
      g.hours += Number(e.hours || 0);
      g.days.add(e.date);
      byUser.set(id, g);
    });

    const list = employees.map((u) => {
      const id = String(u._id);
      const g = byUser.get(id);
      return {
        id,
        name: u.name,
        entries: g ? g.entries : 0,
        hours: g ? g.hours : 0,
        days: g ? g.days.size : 0,
      };
    });

    // people who have entries but are not in the employee list any more
    const known = new Set(list.map((r) => r.id));
    byUser.forEach((g, id) => {
      if (!known.has(id)) {
        list.push({
          id,
          name: g.name,
          entries: g.entries,
          hours: g.hours,
          days: g.days.size,
        });
      }
    });

    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, filtered]);

  /* ---------- month wise rows (only months that have entries) ---------- */
  const monthRows = useMemo(
    () =>
      MONTH_NAMES.map((name, index) => {
        const m = String(index + 1).padStart(2, "0");
        const list = entries.filter((e) => monthOf(e) === m);
        return {
          m,
          name,
          entries: list.length,
          days: new Set(list.map((e) => e.date)).size,
          hours: sumHours(list),
        };
      }).filter((r) => r.entries > 0),
    [entries],
  );

  const maxMonthHours = Math.max(1, ...monthRows.map((r) => r.hours));
  const maxYearHours = Math.max(1, ...years.map((y) => y.hours));

  /* ---------- category wise: department > category ---------- */
  const categoryGroups = useMemo(() => {
    const map = new Map();

    filtered.forEach((e) => {
      const dept = e.department || "Other";
      const cat = e.category || "No category";

      if (!map.has(dept)) {
        map.set(dept, { dept, entries: 0, hours: 0, cats: new Map() });
      }

      const group = map.get(dept);
      group.entries += 1;
      group.hours += Number(e.hours || 0);

      const row = group.cats.get(cat) || { cat, entries: 0, hours: 0 };
      row.entries += 1;
      row.hours += Number(e.hours || 0);
      group.cats.set(cat, row);
    });

    return Array.from(map.values())
      .map((g) => ({
        ...g,
        cats: Array.from(g.cats.values()).sort((a, b) => b.hours - a.hours),
      }))
      .sort((a, b) => b.hours - a.hours);
  }, [filtered]);

  /* ---------- banner numbers ---------- */
  const yearsEntries = years.reduce((s, y) => s + y.entries, 0);
  const yearsHours = years.reduce((s, y) => s + y.hours, 0);
  const workingDays = new Set(filtered.map((e) => e.date)).size;
  const employeeCount = new Set(filtered.map((e) => String(e.user))).size;
  const employeesWithEntries = employeeRows.filter((r) => r.entries > 0).length;

  const monthLabel =
    showMonthFilter && month !== "all"
      ? MONTH_NAMES[Number(month) - 1]
      : "All Months";

  const selectedName =
    employee === "all"
      ? ""
      : employees.find((u) => String(u._id) === employee)?.name || "";

  let stats;
  if (view === "year") {
    stats = [
      { value: years.length, label: "Years" },
      { value: yearsEntries, label: "Entries" },
      { value: fmt(yearsHours), label: "Total Hours" },
    ];
  } else if (view === "employee") {
    stats = [
      { value: employeeRows.length, label: "Employees" },
      { value: employeesWithEntries, label: "With Entries" },
      { value: filtered.length, label: "Entries" },
      { value: fmt(totalHours), label: "Total Hours" },
    ];
  } else {
    stats = [
      { value: monthLabel, label: year },
      { value: filtered.length, label: "Entries" },
      { value: fmt(totalHours), label: "Total Hours" },
      isAdmin && employee === "all"
        ? { value: employeeCount, label: "Employees" }
        : { value: workingDays, label: "Working Days" },
    ];
  }

  const openYear = (y) => {
    setYear(y);
    setMonth("all");
    setView("month");
  };

  const openMonth = (m) => {
    setMonth(m);
    setView("entries");
  };

  const openEmployee = (id) => {
    setEmployee(id);
    setView("entries");
  };

  const tabs = isAdmin
    ? [{ key: "employee", label: "Employee wise" }, ...VIEWS]
    : VIEWS;

  const detailColumns = isAdmin ? 10 : 9;

  return (
    <div className="el-page">
      {/* ----- banner ----- */}
      <header className="el-banner">
        <div>
          <h1>Employee Log Detail</h1>
          <p>
            {isAdmin
              ? "Employee wise, year wise, month wise and category wise record of the work hours."
              : "Year wise, month wise and category wise record of the work hours."}
          </p>
        </div>

        <div className="el-stats">
          {stats.map((s) => (
            <div className="el-stat" key={s.label}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </header>

      {message && <div className="el-message">{message}</div>}

      {/* ----- filters ----- */}
      <section className="el-card el-filters">
        <div className="el-tabs">
          {tabs.map((v) => (
            <button
              type="button"
              key={v.key}
              className={`el-tab ${view === v.key ? "active" : ""}`}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="el-selects">
          {isAdmin && view !== "employee" && (
            <label className="el-select">
              <span>Employee</span>
              <select
                value={employee}
                onChange={(e) => setEmployee(e.target.value)}
              >
                <option value="all">All Employees</option>
                {employees.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {view !== "year" && (
            <label className="el-select">
              <span>Year</span>
              <select value={year} onChange={(e) => setYear(e.target.value)}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
          )}

          {showMonthFilter && (
            <label className="el-select">
              <span>Month</span>
              <select value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="all">All Months</option>
                {MONTH_NAMES.map((name, index) => (
                  <option key={name} value={String(index + 1).padStart(2, "0")}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </section>

      {/* ================= EMPLOYEE WISE ================= */}
      {view === "employee" && isAdmin && (
        <section className="el-card">
          <h3>
            Employee wise - {monthLabel} {year}
          </h3>

          <div className="el-table-wrap">
            <table className="el-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Entries</th>
                  <th>Working Days</th>
                  <th>Total Hours</th>
                  <th className="el-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="el-empty">
                      Loading...
                    </td>
                  </tr>
                ) : employeeRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="el-empty">
                      No employees found.
                    </td>
                  </tr>
                ) : (
                  employeeRows.map((r) => (
                    <tr key={r.id}>
                      <td className="el-strong">{r.name}</td>
                      <td>
                        {r.entries || <span className="el-muted">0</span>}
                      </td>
                      <td>{r.days || <span className="el-muted">0</span>}</td>
                      <td>
                        {r.hours ? (
                          <span className="el-hours">{fmt(r.hours)}</span>
                        ) : (
                          <span className="el-muted">0</span>
                        )}
                      </td>
                      <td className="el-right">
                        <button
                          type="button"
                          className="el-link"
                          onClick={() => openEmployee(r.id)}
                        >
                          View Logs
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {!loading && employeeRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{filtered.length}</td>
                    <td></td>
                    <td>
                      <span className="el-hours el-hours-total">
                        {fmt(totalHours)}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}

      {/* ================= YEAR WISE ================= */}
      {view === "year" && (
        <section className="el-card">
          <h3>Year wise{selectedName ? ` - ${selectedName}` : ""}</h3>

          <div className="el-table-wrap">
            <table className="el-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Entries</th>
                  <th>Total Hours</th>
                  <th className="el-wide">Share</th>
                  <th className="el-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {years.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="el-empty">
                      No entries yet.
                    </td>
                  </tr>
                ) : (
                  years.map((y) => (
                    <tr key={y.year}>
                      <td className="el-strong">{y.year}</td>
                      <td>{y.entries}</td>
                      <td>
                        <span className="el-hours">{y.hours}</span>
                      </td>
                      <td className="el-wide">
                        <progress
                          className="el-progress"
                          value={y.hours}
                          max={maxYearHours}
                        />
                      </td>
                      <td className="el-right">
                        <button
                          type="button"
                          className="el-link"
                          onClick={() => openYear(y.year)}
                        >
                          View months
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {years.length > 0 && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{yearsEntries}</td>
                    <td>
                      <span className="el-hours el-hours-total">
                        {fmt(yearsHours)}
                      </span>
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}

      {/* ================= MONTH WISE ================= */}
      {view === "month" && (
        <section className="el-card">
          <h3>
            Month wise - {year}
            {selectedName ? ` - ${selectedName}` : ""}
          </h3>

          <div className="el-table-wrap">
            <table className="el-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Entries</th>
                  <th>Working Days</th>
                  <th>Total Hours</th>
                  <th className="el-wide">Share</th>
                  <th className="el-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="el-empty">
                      Loading...
                    </td>
                  </tr>
                ) : monthRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="el-empty">
                      No entries in {year}.
                    </td>
                  </tr>
                ) : (
                  monthRows.map((r) => (
                    <tr key={r.m}>
                      <td className="el-strong">{r.name}</td>
                      <td>{r.entries}</td>
                      <td>{r.days}</td>
                      <td>
                        <span className="el-hours">{fmt(r.hours)}</span>
                      </td>
                      <td className="el-wide">
                        <progress
                          className="el-progress"
                          value={r.hours}
                          max={maxMonthHours}
                        />
                      </td>
                      <td className="el-right">
                        <button
                          type="button"
                          className="el-link"
                          onClick={() => openMonth(r.m)}
                        >
                          View entries
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {monthRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td>Total {year}</td>
                    <td>{entries.length}</td>
                    <td>{new Set(entries.map((e) => e.date)).size}</td>
                    <td>
                      <span className="el-hours el-hours-total">
                        {fmt(sumHours(entries))}
                      </span>
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}

      {/* ================= CATEGORY WISE ================= */}
      {view === "category" && (
        <section className="el-card">
          <h3>
            Category wise - {monthLabel} {year}
            {selectedName ? ` - ${selectedName}` : ""}
          </h3>

          <div className="el-table-wrap">
            <table className="el-table">
              <thead>
                <tr>
                  <th>Department / Category</th>
                  <th>Entries</th>
                  <th>Total Hours</th>
                  <th className="el-wide">Share</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="el-empty">
                      Loading...
                    </td>
                  </tr>
                ) : categoryGroups.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="el-empty">
                      No entries in {monthLabel} {year}.
                    </td>
                  </tr>
                ) : (
                  categoryGroups.map((g) => (
                    <React.Fragment key={g.dept}>
                      <tr className="el-group-row">
                        <td>{g.dept}</td>
                        <td>{g.entries}</td>
                        <td>{fmt(g.hours)}</td>
                        <td className="el-wide">
                          {totalHours
                            ? `${fmt((g.hours / totalHours) * 100)}% of total`
                            : ""}
                        </td>
                      </tr>

                      {g.cats.map((c) => (
                        <tr key={`${g.dept}-${c.cat}`}>
                          <td className="el-indent">{c.cat}</td>
                          <td>{c.entries}</td>
                          <td>
                            <span className="el-hours">{fmt(c.hours)}</span>
                          </td>
                          <td className="el-wide">
                            <progress
                              className="el-progress"
                              value={c.hours}
                              max={totalHours || 1}
                            />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                )}
              </tbody>

              {categoryGroups.length > 0 && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{filtered.length}</td>
                    <td>
                      <span className="el-hours el-hours-total">
                        {fmt(totalHours)}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}

      {/* ================= ALL ENTRIES ================= */}
      {view === "entries" && (
        <section className="el-card">
          <h3>
            All entries - {monthLabel} {year}
            {selectedName ? ` - ${selectedName}` : ""}
          </h3>

          <div className="el-table-wrap">
            <table className="el-table el-table-detail">
              <thead>
                <tr>
                  <th>Date</th>
                  {isAdmin && <th>Employee</th>}
                  <th>Time</th>
                  <th>Department</th>
                  <th>Category</th>
                  <th>Project</th>
                  <th>Task</th>
                  <th>Sub-task</th>
                  <th>Hours</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={detailColumns} className="el-empty">
                      Loading...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={detailColumns} className="el-empty">
                      No entries in {monthLabel} {year}.
                    </td>
                  </tr>
                ) : (
                  filtered.map((e) => (
                    <tr key={e._id}>
                      <td className="el-nowrap">{e.date}</td>
                      {isAdmin && <td className="el-strong">{e.employee}</td>}
                      <td className="el-nowrap">
                        {e.startTime && e.endTime ? (
                          <span className="el-time">
                            {showTime(e.startTime)} - {showTime(e.endTime)}
                          </span>
                        ) : (
                          <span className="el-muted">Not saved</span>
                        )}
                      </td>
                      <td>
                        <span className="el-dept">{e.department}</span>
                      </td>
                      <td>{e.category || "-"}</td>
                      <td>{e.project || "-"}</td>
                      <td className="el-strong">{e.task}</td>
                      <td>{e.subTask || "-"}</td>
                      <td>
                        <span className="el-hours">{e.hours}</span>
                      </td>
                      <td className="el-notes-cell" title={e.notes || ""}>
                        {e.notes || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {filtered.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={detailColumns - 2}>Total hours</td>
                    <td>
                      <span className="el-hours el-hours-total">
                        {fmt(totalHours)}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
